// NobleTacticalCard - Priority-based information hierarchy
// Critical (Red) → Urgent (Orange) → Normal (Blue) → Reference (Gray)
// Features: Progressive disclosure, color-coded borders, auto-expand

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Badge, Typography, Space } from 'antd';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  FileOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows } from '../../../theme/noble-tokens';

const { Text, Title: AntTitle } = Typography;

export type TacticalPriority = 'critical' | 'urgent' | 'normal' | 'reference';

export interface NobleTacticalCardProps {
  priority: TacticalPriority;
  title: React.ReactNode;
  summary: React.ReactNode; // Always visible
  details?: React.ReactNode; // Shown when expanded
  icon?: React.ReactNode;
  badge?: string | number;
  defaultExpanded?: boolean;
  onExpand?: (expanded: boolean) => void;
  actions?: React.ReactNode[];
  timestamp?: string;
  className?: string;
}

// Priority configuration
const PRIORITY_CONFIG = {
  critical: {
    color: NobleColors['critical-red-base'],
    bgColor: 'rgba(230, 57, 70, 0.08)',
    borderWidth: '3px',
    icon: <ExclamationCircleOutlined />,
    label: 'CRITICAL',
    autoExpand: true,
    elevation: NobleShadows['elevation-3'],
  },
  urgent: {
    color: NobleColors['warning-amber-base'],
    bgColor: 'rgba(247, 127, 0, 0.06)',
    borderWidth: '2px',
    icon: <WarningOutlined />,
    label: 'URGENT',
    autoExpand: true,
    elevation: NobleShadows['elevation-2'],
  },
  normal: {
    color: NobleColors['command-blue-base'],
    bgColor: NobleColors['level-1-light'],
    borderWidth: '1px',
    icon: <InfoCircleOutlined />,
    label: 'NORMAL',
    autoExpand: false,
    elevation: NobleShadows['elevation-1'],
  },
  reference: {
    color: NobleColors['steel-gray-base'],
    bgColor: NobleColors['level-0-light'],
    borderWidth: '1px',
    icon: <FileOutlined />,
    label: 'REFERENCE',
    autoExpand: false,
    elevation: 'none',
  },
};

// Styled Components
const TacticalCardContainer = styled(motion.div)<{ $priority: TacticalPriority }>`
  border-radius: 12px;
  border: ${({ $priority }) => PRIORITY_CONFIG[$priority].borderWidth} solid ${({ $priority }) => PRIORITY_CONFIG[$priority].color};
  ${({ $priority }) => $priority === 'reference' && 'border-style: dashed;'}
  background: ${({ $priority }) => PRIORITY_CONFIG[$priority].bgColor};
  box-shadow: ${({ $priority }) => PRIORITY_CONFIG[$priority].elevation};
  margin-bottom: ${NobleSpacing.md};
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease-out;

  &:hover {
    box-shadow: ${({ $priority }) => $priority !== 'reference' ? NobleShadows['elevation-2'] : 'none'};
    transform: ${({ $priority }) => $priority !== 'reference' ? 'translateY(-2px)' : 'none'};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const CardHeader = styled.div<{ $priority: TacticalPriority; $clickable: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${NobleSpacing.md};
  padding: ${NobleSpacing.lg};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  user-select: none;

  @media (max-width: 576px) {
    padding: ${NobleSpacing.md};
  }
  position: relative;

  &:hover {
    background: ${({ $clickable, $priority }) =>
      $clickable && $priority !== 'reference' ? 'rgba(0, 0, 0, 0.02)' : 'transparent'};
  }
`;

const PriorityIcon = styled.div<{ $priority: TacticalPriority }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $priority }) => PRIORITY_CONFIG[$priority].color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0; // Allow text truncation
`;

const CardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${NobleColors['steel-gray-dark']};
  margin-bottom: ${NobleSpacing.xxs};
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
`;

const PriorityBadge = styled.span<{ $priority: TacticalPriority }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: ${({ $priority }) => PRIORITY_CONFIG[$priority].color};
  color: white;
`;

const CardSummary = styled.div`
  font-size: 14px;
  color: ${NobleColors['steel-gray-base']};
  line-height: 1.5;
  margin-bottom: ${NobleSpacing.xs};
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.md};
  flex-wrap: wrap;
  margin-top: ${NobleSpacing.sm};
`;

const MetaItem = styled.span`
  font-size: 12px;
  color: ${NobleColors['steel-gray-base']};
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xxs};
`;

const ExpandButton = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${NobleColors['steel-gray-dark']};
  flex-shrink: 0;
  transition: all 0.2s ease-out;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: scale(1.1);
  }
`;

const CardDetails = styled(motion.div)`
  padding: 0 ${NobleSpacing.lg} ${NobleSpacing.lg} ${NobleSpacing.lg};
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.5);
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.sm};
  flex-wrap: wrap;
  margin-top: ${NobleSpacing.md};
  padding-top: ${NobleSpacing.md};
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
`;

// Main Component
export const NobleTacticalCard: React.FC<NobleTacticalCardProps> = ({
  priority,
  title,
  summary,
  details,
  icon,
  badge,
  defaultExpanded,
  onExpand,
  actions,
  timestamp,
  className,
}) => {
  const config = PRIORITY_CONFIG[priority];
  const [isExpanded, setIsExpanded] = useState(defaultExpanded !== undefined ? defaultExpanded : config.autoExpand);

  const hasExpandableContent = !!details || (actions && actions.length > 0);

  const handleToggleExpand = () => {
    if (!hasExpandableContent) return;
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpand?.(newExpanded);
  };

  return (
    <TacticalCardContainer
      $priority={priority}
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      <CardHeader $priority={priority} $clickable={Boolean(hasExpandableContent)} onClick={handleToggleExpand}>
        {/* Priority Icon */}
        <PriorityIcon $priority={priority}>
          {icon || config.icon}
        </PriorityIcon>

        {/* Content */}
        <CardContent>
          <CardTitle>
            {title}
            <PriorityBadge $priority={priority}>{config.label}</PriorityBadge>
            {badge && (
              <Badge
                count={badge}
                style={{
                  backgroundColor: config.color,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                }}
              />
            )}
          </CardTitle>

          <CardSummary>{summary}</CardSummary>

          <CardMeta>
            {timestamp && (
              <MetaItem>
                <InfoCircleOutlined />
                {timestamp}
              </MetaItem>
            )}
          </CardMeta>
        </CardContent>

        {/* Expand Button */}
        {hasExpandableContent && (
          <ExpandButton>
            {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </ExpandButton>
        )}
      </CardHeader>

      {/* Expandable Details */}
      <AnimatePresence>
        {isExpanded && hasExpandableContent && (
          <CardDetails
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {details && <div style={{ marginTop: NobleSpacing.md }}>{details}</div>}

            {actions && actions.length > 0 && (
              <CardActions>
                {actions.map((action, index) => (
                  <React.Fragment key={index}>{action}</React.Fragment>
                ))}
              </CardActions>
            )}
          </CardDetails>
        )}
      </AnimatePresence>
    </TacticalCardContainer>
  );
};

// Pre-configured tactical cards for common use cases

interface CriticalAlertCardProps extends Omit<NobleTacticalCardProps, 'priority'> {}

export const CriticalAlertCard: React.FC<CriticalAlertCardProps> = (props) => (
  <NobleTacticalCard priority="critical" {...props} />
);

interface UrgentTaskCardProps extends Omit<NobleTacticalCardProps, 'priority'> {}

export const UrgentTaskCard: React.FC<UrgentTaskCardProps> = (props) => (
  <NobleTacticalCard priority="urgent" {...props} />
);

interface NormalCardProps extends Omit<NobleTacticalCardProps, 'priority'> {}

export const NormalCard: React.FC<NormalCardProps> = (props) => (
  <NobleTacticalCard priority="normal" {...props} />
);

interface ReferenceCardProps extends Omit<NobleTacticalCardProps, 'priority'> {}

export const ReferenceCard: React.FC<ReferenceCardProps> = (props) => (
  <NobleTacticalCard priority="reference" {...props} />
);

